"use client";

import { Suspense, useState, useEffect, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import { Search, Filter, Calendar, Tag } from "lucide-react";
import InbCard from "@/components/inb/InbCard";
import { SkeletonCard } from "@/components/common/Skeleton";

interface InbTag {
    id: string;
    name: string;
    slug: string;
    color: string;
}

interface Inb {
    id: string;
    title: string;
    description: string;
    eventDate: string;
    upvotes: number;
    downvotes: number;
    score: number;
    author: { name: string | null; email: string };
    tags: InbTag[];
}

interface TagOption {
    id: string;
    name: string;
    slug: string;
    category: string;
    color: string;
}

function InbyContent() {
    const searchParams = useSearchParams();
    const initialTag = searchParams.get("tag") || "";

    const [inbs, setInbs] = useState<Inb[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [sort, setSort] = useState("score");
    const [selectedTag, setSelectedTag] = useState(initialTag);
    const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
    const months = [
        "Styczeń",
        "Luty",
        "Marzec",
        "Kwiecień",
        "Maj",
        "Czerwiec",
        "Lipiec",
        "Sierpień",
        "Wrzesień",
        "Październik",
        "Listopad",
        "Grudzień",
    ];

    const fetchInbs = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: "20",
                sort,
            });
            if (search) params.set("search", search);
            if (year) params.set("year", year);
            if (month) params.set("month", month);
            if (selectedTag) params.set("tag", selectedTag);

            const response = await fetch(`/api/inby?${params}`);
            const data = await response.json();

            setInbs(data.inbs || []);
            setTotalPages(data.pagination?.totalPages || 1);
        } catch (error) {
            console.error("Error fetching inbs:", error);
        } finally {
            setIsLoading(false);
        }
    }, [page, search, sort, year, month, selectedTag]);

    // Fetch available tags on mount
    useEffect(() => {
        const fetchTags = async () => {
            try {
                const response = await fetch("/api/tags");
                const data = await response.json();
                // Only set tags if response is an array
                if (Array.isArray(data)) {
                    setAvailableTags(data);
                }
            } catch (error) {
                console.error("Error fetching tags:", error);
            }
        };
        fetchTags();
    }, []);

    useEffect(() => {
        fetchInbs();
    }, [fetchInbs]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        fetchInbs();
    };

    return (
        <div className="container mx-auto px-4 py-8 animate-fadeIn">
            <h1 className="text-3xl font-bold mb-8">Wszystkie inby</h1>

            {/* Filters */}
            <div className="card mb-8">
                <form onSubmit={handleSearch} className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                            size={18}
                        />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Szukaj inb..."
                            className="input !pl-10"
                        />
                    </div>

                    {/* Filter row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                <Calendar size={14} />
                                Rok
                            </label>
                            <select
                                value={year}
                                onChange={(e) => {
                                    setYear(e.target.value);
                                    setPage(1);
                                }}
                                className="input"
                            >
                                <option value="">Wszystkie</option>
                                {years.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                <Calendar size={14} />
                                Miesiąc
                            </label>
                            <select
                                value={month}
                                onChange={(e) => {
                                    setMonth(e.target.value);
                                    setPage(1);
                                }}
                                className="input"
                                disabled={!year}
                            >
                                <option value="">Wszystkie</option>
                                {months.map((m, i) => (
                                    <option key={i} value={i + 1}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                <Filter size={14} />
                                Sortowanie
                            </label>
                            <select
                                value={sort}
                                onChange={(e) => {
                                    setSort(e.target.value);
                                    setPage(1);
                                }}
                                className="input"
                            >
                                <option value="score">Najlepsze</option>
                                <option value="newest">Najnowsze</option>
                                <option value="oldest">Najstarsze</option>
                                <option value="upvotes">Najwięcej upvotes</option>
                            </select>
                        </div>

                        <div>
                            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                <Tag size={14} />
                                Tag
                            </label>
                            <select
                                value={selectedTag}
                                onChange={(e) => {
                                    setSelectedTag(e.target.value);
                                    setPage(1);
                                }}
                                className="input"
                            >
                                <option value="">Wszystkie</option>
                                {availableTags.map((tag) => (
                                    <option key={tag.id} value={tag.slug}>
                                        {tag.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-end">
                            <button type="submit" className="btn btn-primary w-full">
                                Szukaj
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Results */}
            {isLoading ? (
                <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                        <SkeletonCard key={i} />
                    ))}
                </div>
            ) : inbs.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-400">Nie znaleziono żadnych inb</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {inbs.map((inb) => (
                        <InbCard
                            key={inb.id}
                            id={inb.id}
                            title={inb.title}
                            description={inb.description}
                            eventDate={new Date(inb.eventDate)}
                            upvotes={inb.upvotes}
                            downvotes={inb.downvotes}
                            authorName={inb.author?.name || inb.author?.email}
                            tags={inb.tags}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="btn btn-ghost"
                    >
                        ← Poprzednia
                    </button>
                    <span className="flex items-center px-4 text-gray-400">
                        {page} / {totalPages}
                    </span>
                    <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="btn btn-ghost"
                    >
                        Następna →
                    </button>
                </div>
            )}
        </div>
    );
}

export default function InbyPage() {
    return (
        <Suspense
            fallback={
                <div className="container mx-auto px-4 py-8">
                    <h1 className="text-3xl font-bold mb-8">Wszystkie inby</h1>
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                </div>
            }
        >
            <InbyContent />
        </Suspense>
    );
}
